import React, { useState, useEffect } from "react";
import {
  Box,
  Text,
  VStack,
  HStack,
  Button,
  Checkbox,
  Collapse,
  useDisclosure,
  Icon,
  Divider,
} from "@chakra-ui/react";
import { ChevronDownIcon, ChevronUpIcon } from "@chakra-ui/icons";

export type PlacementType = "facebook" | "instagram";

export interface PlacementOption {
  label: string;
  value: string;
  type: PlacementType;
}

interface PlacementGroup {
  name: string;
  options: PlacementOption[];
}

interface PlacementSelectorProps {
  facebookPlacements: PlacementOption[];
  instagramPlacements: PlacementOption[];
  selectedFacebook: string[];
  selectedInstagram: string[];
  onPlacementChange: (placements: {
    facebook: string[];
    instagram: string[];
  }) => void;
  onSave?: () => void;
  disabled?: boolean;
}

const PlacementSelector: React.FC<PlacementSelectorProps> = ({
  facebookPlacements,
  instagramPlacements,
  selectedFacebook,
  selectedInstagram,
  onPlacementChange,
  onSave,
  disabled = false,
}) => {
  const [localFacebook, setLocalFacebook] =
    useState<string[]>(selectedFacebook);
  const [localInstagram, setLocalInstagram] =
    useState<string[]>(selectedInstagram);

  // Sync local state with prop changes, but only when actual values change
  useEffect(() => {
    const currentFb = new Set(localFacebook);
    const incomingFb = new Set(selectedFacebook);
    const currentIg = new Set(localInstagram);
    const incomingIg = new Set(selectedInstagram);

    const fbChanged =
      currentFb.size !== incomingFb.size ||
      Array.from(currentFb).some((v) => !incomingFb.has(v));
    const igChanged =
      currentIg.size !== incomingIg.size ||
      Array.from(currentIg).some((v) => !incomingIg.has(v));

    if (fbChanged) setLocalFacebook(selectedFacebook);
    if (igChanged) setLocalInstagram(selectedInstagram);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFacebook.join(","), selectedInstagram.join(",")]);

  const { isOpen: isFacebookOpen, onToggle: onFacebookToggle } = useDisclosure({
    defaultIsOpen: true,
  });
  const { isOpen: isInstagramOpen, onToggle: onInstagramToggle } =
    useDisclosure({ defaultIsOpen: true });

  const handlePlacementToggle = (
    placementValue: string,
    placementType: PlacementType
  ) => {
    if (placementType === "facebook") {
      setLocalFacebook((prev) => {
        const next = prev.includes(placementValue)
          ? prev.filter((v) => v !== placementValue)
          : [...prev, placementValue];
        onPlacementChange({ facebook: next, instagram: localInstagram });
        return next;
      });
    } else {
      setLocalInstagram((prev) => {
        const next = prev.includes(placementValue)
          ? prev.filter((v) => v !== placementValue)
          : [...prev, placementValue];
        onPlacementChange({ facebook: localFacebook, instagram: next });
        return next;
      });
    }
  };

  const handleSave = () => {
    if (onSave) {
      onSave();
    }
  };

  const isAllSelected = (placements: PlacementOption[]) => {
    const selectedSet =
      placements[0]?.type === "facebook"
        ? new Set(localFacebook)
        : new Set(localInstagram);
    return placements.every((placement) => selectedSet.has(placement.value));
  };

  const isSomeSelected = (placements: PlacementOption[]) => {
    const selectedSet =
      placements[0]?.type === "facebook"
        ? new Set(localFacebook)
        : new Set(localInstagram);
    return placements.some((placement) => selectedSet.has(placement.value));
  };

  const handleSelectAll = (
    placements: PlacementOption[],
    isSelectAll: boolean
  ) => {
    const placementType = placements[0]?.type;
    const placementValues = placements.map((p) => p.value);

    if (placementType === "facebook") {
      const next = isSelectAll ? placementValues : [];
      setLocalFacebook(next);
      onPlacementChange({ facebook: next, instagram: localInstagram });
    } else {
      const next = isSelectAll ? placementValues : [];
      setLocalInstagram(next);
      onPlacementChange({ facebook: localFacebook, instagram: next });
    }
  };

  const renderPlacementGroup = (
    groupName: string,
    placements: PlacementOption[],
    isOpen: boolean,
    onToggle: () => void,
    isAllSelected: boolean,
    isSomeSelected: boolean
  ) => (
    <Box
      w="full"
      borderRadius="lg"
      border="1px solid"
      borderColor="gray.200"
      bg="white"
      overflow="hidden"
    >
      <HStack
        w="full"
        p={4}
        justifyContent="space-between"
        cursor="pointer"
        onClick={onToggle}
        _hover={{ bg: "gray.50" }}
      >
        <HStack spacing={3}>
          <Checkbox
            isChecked={isAllSelected}
            isIndeterminate={isSomeSelected && !isAllSelected}
            onChange={(e) => handleSelectAll(placements, e.target.checked)}
            onClick={(e) => e.stopPropagation()}
            colorScheme="blue"
            size="md"
          />
          <Text fontWeight="semibold" fontSize="md">
            {groupName}
          </Text>
        </HStack>
        <Icon
          as={isOpen ? ChevronUpIcon : ChevronDownIcon}
          w={5}
          h={5}
          color="blue.500"
        />
      </HStack>

      <Collapse in={isOpen}>
        <VStack spacing={0} align="stretch">
          <Divider />
          {placements.map((placement, index) => (
            <Box key={`${placement.type}-${placement.value}`}>
              <HStack
                p={4}
                spacing={3}
                _hover={{ bg: "gray.50" }}
                cursor="pointer"
                onClick={() =>
                  handlePlacementToggle(placement.value, placement.type)
                }
              >
                <Checkbox
                  isChecked={
                    placement.type === "facebook"
                      ? localFacebook.includes(placement.value)
                      : localInstagram.includes(placement.value)
                  }
                  onChange={() =>
                    handlePlacementToggle(placement.value, placement.type)
                  }
                  colorScheme="blue"
                  size="md"
                />
                <Text fontSize="sm" color="gray.700">
                  {placement.label}
                </Text>
              </HStack>
              {index < placements.length - 1 && <Divider />}
            </Box>
          ))}
        </VStack>
      </Collapse>
    </Box>
  );

  return (
    <VStack w="full" spacing={6} align="stretch">
      {/* Header */}
      <VStack spacing={2} align="flex-start">
        <HStack spacing={2}>
          <Box
            w={6}
            h={6}
            borderRadius="full"
            bg="green.500"
            display="flex"
            alignItems="center"
            justifyContent="center"
          >
            <Text color="white" fontSize="xs" fontWeight="bold">
              ✓
            </Text>
          </Box>
          <Text fontSize="lg" fontWeight="bold">
            Placement & Destination
          </Text>
        </HStack>
        <Text fontSize="sm" color="gray.600">
          Where your ad appears and what it links to
        </Text>
      </VStack>

      {/* Placement Controls */}
      <VStack spacing={4} align="stretch">
        <HStack justifyContent="space-between" align="flex-start">
          <VStack align="flex-start" spacing={1}>
            <Text fontWeight="semibold" fontSize="md">
              Placement controls
            </Text>
            <Text fontSize="sm" color="gray.600">
              Custom placement
            </Text>
          </VStack>
          <Button
            colorScheme="blue"
            size="sm"
            onClick={handleSave}
            disabled={disabled}
            _hover={{ bg: "blue.600" }}
          >
            Save
          </Button>
        </HStack>

        {/* Facebook Placements */}
        {renderPlacementGroup(
          "Facebook",
          facebookPlacements,
          isFacebookOpen,
          onFacebookToggle,
          isAllSelected(facebookPlacements),
          isSomeSelected(facebookPlacements)
        )}

        {/* Instagram Placements */}
        {renderPlacementGroup(
          "Instagram",
          instagramPlacements,
          isInstagramOpen,
          onInstagramToggle,
          isAllSelected(instagramPlacements),
          isSomeSelected(instagramPlacements)
        )}
      </VStack>
    </VStack>
  );
};

export default PlacementSelector;
